/**
 * Allys Featured Properties Carousel
 * 
 * Showcase properties that are featured for the Homepage section.
 * Only displays properties with homepage-related placement types:
 * - HOMEPAGE_SLIDER
 * - HOMEPAGE_GRID
 * - HOMEPAGE_SPOTLIGHT
 * - HOMEPAGE_SHOWCASE
 * - HOMEPAGE_BANNER
 * 
 * Features elegant glass morphism cards and smooth animations.
 */

'use client';

import { FeaturedPropertiesCarousel as CoreFeaturedPropertiesCarousel } from '../../../../src/components/properties';
import type { Property } from '../../../../src/lib/api/clients/properties';
import type { FeaturedPlacementSummary } from '../../../../src/lib/api/clients/featured-content';
import { isHomepagePlacement } from '../../../../src/lib/api/clients/featured-content';

/** Property with optional featured placements */
interface PropertyWithFeatured extends Property {
  featuredPlacements?: FeaturedPlacementSummary[];
}

export interface AllysFeaturedPropertiesCarouselProps {
  properties: PropertyWithFeatured[];
  title?: string;
  subtitle?: string;
  onPropertyClick?: (property: PropertyWithFeatured) => void;
  /**
   * Maximum number of properties to display
   * @default 6
   */
  maxItems?: number;
  /**
   * Messages for translations
   */
  messages?: Record<string, unknown>;
  /**
   * Current locale
   */
  locale?: 'en' | 'es' | 'fr';
}

// Helper to safely get string from messages
const safeGetString = (obj: unknown, defaultValue: string): string => {
  if (typeof obj === 'string') return obj;
  return defaultValue;
};

/**
 * Filter properties to only those featured for the Homepage section.
 * Returns properties sorted by priority (highest first).
 */
function getHomepageFeaturedProperties(
  properties: PropertyWithFeatured[],
  maxCount: number
): PropertyWithFeatured[] {
  // Filter to properties with homepage placements
  const homepageFeatured = properties.filter(p =>
    p.featuredPlacements?.some(fp => isHomepagePlacement(fp.placementType))
  );
  
  // Sort by priority (highest first)
  const sorted = homepageFeatured.sort((a, b) => {
    const getPriority = (prop: PropertyWithFeatured) => {
      if (!prop.featuredPlacements) return 0;
      const homepagePlacement = prop.featuredPlacements.find(fp => 
        isHomepagePlacement(fp.placementType)
      );
      return homepagePlacement?.priority || 0;
    };
    return getPriority(b) - getPriority(a);
  });
  
  return sorted.slice(0, maxCount);
}

export function AllysFeaturedPropertiesCarousel({
  properties,
  title,
  subtitle,
  onPropertyClick,
  maxItems = 6,
  messages,
  locale = 'en',
}: AllysFeaturedPropertiesCarouselProps) {
  // Get translations from messages
  const carouselMsg = messages?.featuredCarousel as Record<string, unknown> | undefined;
  const displayTitle = title || safeGetString(carouselMsg?.title, 'Featured Properties');
  const displaySubtitle = subtitle || safeGetString(carouselMsg?.subtitle, 'Explore our curated selection of exceptional properties');
  
  // Filter to only properties featured for the homepage
  // Only shows properties with placement types: HOMEPAGE_SLIDER, HOMEPAGE_GRID, etc.
  const homepageProperties = getHomepageFeaturedProperties(properties, maxItems);
  
  // Don't render if no homepage-featured properties
  if (homepageProperties.length === 0) {
    return null;
  }
  
  return (
    <CoreFeaturedPropertiesCarousel
      properties={homepageProperties}
      title={displayTitle}
      subtitle={displaySubtitle}
      variant="fullwidth"
      className="bg-linear-to-br from-[#040813] via-[#121C3B] to-[#040813]"
      maxItems={maxItems}
      locale={locale}
      {...(messages && { messages })}
      {...(onPropertyClick && { onPropertyClick })}
    />
  );
}

